import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { saveFile, removeStorageFile } from '../utils/FileManager';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';

// ============================================================
// TRAINING MODULES
// ============================================================

const getTrainingModules = async ({ pageParam = 0, searchTerm, filters = null, sortKey = null, sortDirection = null }) => {
    const orgID = await getCurrentOrgID();
    const filterValues = filters?.reduce((acc, filter) => ({ ...acc, [filter.id]: filter.value }), {});

    const { error, data } = await supabase.rpc('get_training_modules', {
        p_org_id: orgID,
        p_teacher_id: filterValues?.teacher?.id || null,
        p_status: filterValues?.status || null,
        p_search_term: searchTerm || null,
        p_offset: pageParam * 25,
        p_limit: 25,
        p_sort_key: sortKey || null,
        p_sort_direction: sortDirection || null
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingModules", input: { organizationID: orgID, filters, searchTerm } }, `Something went wrong fetching the Training Modules`);
        return { nextPage: undefined, data: [] };
    }

    return { nextPage: data.length < 25 ? undefined : pageParam + 1, data };
};

const getTrainingModule = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('TrainingModules').select(`*,
        Teacher:People!TrainingModules_teacherID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Teachers:TrainingModuleTeachers(id, Person:People!TrainingModuleTeachers_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        Classes:TrainingClasses(*,
            Requirements:TrainingRequirements(*, Completions:TrainingRequirementCompletions(id, enrollmentID, completedAt)),
            Completions:TrainingClassCompletions(id, enrollmentID, completedAt, classInstanceID),
            Instances:TrainingClassInstances(id, classID, scheduledAt, notes, organizationID, createdAt, termID, teacherID, taskInstanceID,
                Teacher:People!TrainingClassInstances_teacherID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
                TaskInstance:TaskInstances(id, Assignees:TaskInstancePeople(assignmentResponse, personID))
            ),
            Files:TrainingFileAttachments(*)
        ),
        Terms:TrainingClassTerms(id, name, description, createdAt),
        Requirements:TrainingRequirements(*, Completions:TrainingRequirementCompletions(id, enrollmentID, completedAt)),
        Enrollments:TrainingEnrollments(*, 
            Person:People!TrainingEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
            ClassCompletions:TrainingClassCompletions(id, classID, completedAt, classInstanceID),
            RequirementCompletions:TrainingRequirementCompletions(id, requirementID, completedAt)
        )
    `)
    .eq('organizationID', orgID)
    .eq('id', id)
    .order('sortOrder', { referencedTable: 'Classes', ascending: true })
    .order('sortOrder', { referencedTable: 'Requirements', ascending: true })
    .order('sortOrder', { referencedTable: 'Classes.Requirements', ascending: true })
    .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingModule", input: { organizationID: orgID, id } }, `Something went wrong fetching the Training Module`);
        return null;
    }

    if (data) {
        // Flatten Teachers from join table into simple Person array
        data.Teachers = (data.Teachers || []).map(t => t.Person).filter(Boolean);

        // Fetch prerequisite module separately (self-referential FK not in PostgREST schema cache)
        if (data.prerequisiteModuleID) {
            const { data: prereq } = await supabase.from('TrainingModules')
                .select('id, name')
                .eq('id', data.prerequisiteModuleID)
                .maybeSingle();
            data.PrerequisiteModule = prereq || null;
        } else {
            data.PrerequisiteModule = null;
        }
    }

    return data;
};

const saveTrainingModule = async (details) => {
    const orgID = await getCurrentOrgID();
    const originalValues = details.originalValues || null;

    const moduleData = {
        id: details.id,
        organizationID: orgID,
        name: details.name,
        description: details.description,
        status: details.status || 'ACTIVE',
        teacherID: details.Teachers?.[0]?.id || details.teacherID || null,
        prerequisiteModuleID: details.prerequisiteModuleID || null,
    };

    const teachers = details.Teachers || [];

    if (!details.id) {
        return createTrainingModule(moduleData, details.Classes, teachers);
    } else {
        return updateTrainingModule(moduleData, details.Classes, teachers, originalValues);
    }
};

const createTrainingModule = async (moduleData, classes = [], teachers = []) => {
    const { error, data } = await supabase.from('TrainingModules').insert(moduleData).select().maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "createTrainingModule", error, moduleData }, `Something went wrong creating the Training Module`);
        throw Error();
    }

    // Add classes and their class-level requirements
    if (classes.length > 0) {
        for (let i = 0; i < classes.length; i++) {
            const c = classes[i];
            const { error: classError, data: classData } = await supabase.from('TrainingClasses').insert({
                organizationID: moduleData.organizationID,
                moduleID: data.id,
                name: c.name,
                description: c.description,
                sortOrder: i,
            }).select('id').maybeSingle();

            if (classError) {
                ErrorHandler(Error(classError.message), { mutation: "createTrainingModule-class", classError }, `Module created but something went wrong adding a class`);
                continue;
            }

            const classReqs = (c.Requirements || []).filter(r => r.name?.trim());
            if (classReqs.length > 0 && classData?.id) {
                const { error: crError } = await supabase.from('TrainingRequirements').insert(
                    classReqs.map((r, ri) => ({
                        organizationID: moduleData.organizationID,
                        moduleID: data.id,
                        classID: classData.id,
                        name: r.name,
                        description: r.description,
                        sortOrder: ri,
                    }))
                );
                if (crError) {
                    ErrorHandler(Error(crError.message), { mutation: "createTrainingModule-classReqs", crError }, `Class created but something went wrong adding its requirements`);
                }
            }
        }
    }

    // Add teachers to join table
    if (teachers.length > 0) {
        const { error: teacherError } = await supabase.from('TrainingModuleTeachers').insert(
            teachers.map(t => ({
                organizationID: moduleData.organizationID,
                moduleID: data.id,
                personID: t.id,
            }))
        );
        if (teacherError) {
            ErrorHandler(Error(teacherError.message), { mutation: "createTrainingModule-teachers", teacherError }, `Module created but something went wrong adding teachers`);
        }
    }

    Toast.delayShow({ type: 'success', text2: `Training Module "${data.name}" created!` });

    insertActivity({ subjectType: 'TrainingModule', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

    return data;
};

const updateTrainingModule = async (moduleData, classes = [], teachers = [], originalValues = null) => {
    moduleData.updatedAt = new Date();

    const { error, data } = await supabase.from('TrainingModules').update(moduleData).eq('id', moduleData.id).select().maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "updateTrainingModule", error, moduleData }, `Something went wrong updating the Training Module`);
        throw Error();
    }

    const moduleID = data.id;
    const orgID = moduleData.organizationID;

    // Sync teachers: delete all existing, re-insert
    if (teachers.length >= 0) {
        await supabase.from('TrainingModuleTeachers').delete().eq('moduleID', moduleID);
        if (teachers.length > 0) {
            const { error: teacherError } = await supabase.from('TrainingModuleTeachers').insert(
                teachers.map(t => ({
                    organizationID: orgID,
                    moduleID: moduleID,
                    personID: t.id,
                }))
            );
            if (teacherError) {
                ErrorHandler(Error(teacherError.message), { mutation: "updateTrainingModule-teachers", teacherError }, `Module updated but something went wrong syncing teachers`);
            }
        }
    }

    // Sync classes and their requirements
    // Get existing classes to determine what to delete
    const { data: existingClasses } = await supabase.from('TrainingClasses')
        .select('id, Requirements:TrainingRequirements(id)')
        .eq('moduleID', moduleID);

    const existingClassIds = new Set((existingClasses || []).map(c => c.id));
    const updatedClassIds = new Set();

    for (let i = 0; i < classes.length; i++) {
        const c = classes[i];
        const classData = {
            name: c.name,
            description: c.description,
            sortOrder: i,
        };

        let classID;
        if (c.id && existingClassIds.has(c.id)) {
            // Update existing class
            const { error: updateError } = await supabase.from('TrainingClasses')
                .update(classData)
                .eq('id', c.id);
            if (updateError) {
                ErrorHandler(Error(updateError.message), { mutation: "updateTrainingModule-class", updateError }, `Something went wrong updating class`);
                continue;
            }
            classID = c.id;
            updatedClassIds.add(c.id);
        } else {
            // Create new class
            classData.moduleID = moduleID;
            classData.organizationID = orgID;

            const { error: createError, data: newClass } = await supabase.from('TrainingClasses')
                .insert(classData)
                .select('id')
                .maybeSingle();
            if (createError) {
                ErrorHandler(Error(createError.message), { mutation: "updateTrainingModule-class", createError }, `Something went wrong creating class`);
                continue;
            }
            classID = newClass.id;
            updatedClassIds.add(newClass.id);
        }

        // Sync requirements for this class
        const classReqs = (c.Requirements || []).filter(r => r.name?.trim());
        const updatedReqIds = new Set();

        for (let ri = 0; ri < classReqs.length; ri++) {
            const r = classReqs[ri];
            const reqData = {
                name: r.name,
                description: r.description,
                sortOrder: ri,
            };

            if (r.id) {
                // Update existing requirement
                const { error: reqUpdateError } = await supabase.from('TrainingRequirements')
                    .update(reqData)
                    .eq('id', r.id);
                if (reqUpdateError) {
                    ErrorHandler(Error(reqUpdateError.message), { mutation: "updateTrainingModule-req", reqUpdateError }, `Something went wrong updating requirement`);
                } else {
                    updatedReqIds.add(r.id);
                }
            } else {
                // Create new requirement
                reqData.organizationID = orgID;
                reqData.moduleID = moduleID;
                reqData.classID = classID;

                const { error: reqCreateError } = await supabase.from('TrainingRequirements')
                    .insert(reqData);
                if (reqCreateError) {
                    ErrorHandler(Error(reqCreateError.message), { mutation: "updateTrainingModule-req", reqCreateError }, `Something went wrong creating requirement`);
                }
            }
        }

        // Delete removed requirements for this class
        const reqsToDelete = (existingClasses || [])
            .find(ec => ec.id === classID)?.Requirements
            ?.filter(r => r.id && !updatedReqIds.has(r.id))
            .map(r => r.id) || [];

        if (reqsToDelete.length > 0) {
            await supabase.from('TrainingRequirements').delete().in('id', reqsToDelete);
        }
    }

    // Delete removed classes (and their requirements will cascade delete)
    const classesToDelete = [...existingClassIds].filter(id => !updatedClassIds.has(id));
    if (classesToDelete.length > 0) {
        await supabase.from('TrainingClasses').delete().in('id', classesToDelete);
    }

    Toast.delayShow({ type: 'success', text2: `Training Module "${data.name}" updated!` });

    const scalarDetails = originalValues ? buildUpdateDetails(originalValues, moduleData, {
        name: { label: 'Name' },
        description: { label: 'Description' },
        status: { label: 'Status' },
    }) : null;
    insertActivity({ subjectType: 'TrainingModule', subjectID: data.id, subjectName: data.name, verb: 'update', details: scalarDetails }).catch(() => {});

    return data;
};

const deleteTrainingModule = async (moduleID) => {
    const { data: existing } = await supabase.from('TrainingModules').select('name').eq('id', moduleID).maybeSingle();

    const { error } = await supabase.from('TrainingModules').delete().eq('id', moduleID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteTrainingModule", error, moduleID }, `Something went wrong deleting the Training Module`);
        throw Error();
    }

    insertActivity({ subjectType: 'TrainingModule', subjectID: moduleID, subjectName: existing?.name, verb: 'delete' }).catch(() => {});

    Toast.delayShow({ type: 'success', text2: 'Successfully deleted Training Module' });
};


// ============================================================
// TRAINING CLASSES
// ============================================================

const saveTrainingClass = async (details) => {
    const orgID = await getCurrentOrgID();

    const classData = {
        id: details.id,
        organizationID: orgID,
        moduleID: details.moduleID,
        name: details.name,
        description: details.description,
        sortOrder: details.sortOrder ?? 0,
    };

    if (!details.id) {
        const { error, data } = await supabase.from('TrainingClasses').insert(classData).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: "createTrainingClass", error, classData }, `Something went wrong creating the class`);
            throw Error();
        }
        Toast.delayShow({ type: 'success', text2: `Class "${data.name}" created!` });
        return data;
    } else {
        const { error, data } = await supabase.from('TrainingClasses').update(classData).eq('id', classData.id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: "updateTrainingClass", error, classData }, `Something went wrong updating the class`);
            throw Error();
        }
        Toast.delayShow({ type: 'success', text2: `Class "${data.name}" updated!` });
        return data;
    }
};

const deleteTrainingClass = async (classID) => {
    const { error, data } = await supabase.from('TrainingClasses').delete().eq('id', classID).select().maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteTrainingClass", error, classID }, `Something went wrong deleting the class`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Successfully deleted class' });
    return data;
};

const reorderTrainingClasses = async (classes) => {
    const updates = classes.map((c, i) => supabase.from('TrainingClasses').update({ sortOrder: i }).eq('id', c.id));
    await Promise.all(updates);
};


// ============================================================
// TRAINING REQUIREMENTS
// ============================================================

const saveTrainingRequirement = async (details) => {
    const orgID = await getCurrentOrgID();

    const reqData = {
        id: details.id,
        organizationID: orgID,
        moduleID: details.moduleID,
        classID: details.classID || null,
        name: details.name,
        description: details.description,
        dueDate: details.dueDate || null,
        sortOrder: details.sortOrder ?? 0,
    };

    if (!details.id) {
        const { error, data } = await supabase.from('TrainingRequirements').insert(reqData).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: "createTrainingRequirement", error, reqData }, `Something went wrong creating the requirement`);
            throw Error();
        }
        Toast.delayShow({ type: 'success', text2: `Requirement "${data.name}" created!` });
        return data;
    } else {
        const { error, data } = await supabase.from('TrainingRequirements').update(reqData).eq('id', reqData.id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: "updateTrainingRequirement", error, reqData }, `Something went wrong updating the requirement`);
            throw Error();
        }
        Toast.delayShow({ type: 'success', text2: `Requirement "${data.name}" updated!` });
        return data;
    }
};

const deleteTrainingRequirement = async (requirementID) => {
    const { error } = await supabase.from('TrainingRequirements').delete().eq('id', requirementID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteTrainingRequirement", error, requirementID }, `Something went wrong deleting the requirement`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Successfully deleted requirement' });
};


// ============================================================
// ENROLLMENTS
// ============================================================

const checkPrerequisite = async (moduleID, personIDs) => {
    const { data: module } = await supabase.from('TrainingModules')
        .select('prerequisiteModuleID')
        .eq('id', moduleID)
        .maybeSingle();

    if (!module?.prerequisiteModuleID) return { eligible: personIDs, ineligible: [] };

    const { data: completedEnrollments } = await supabase.from('TrainingEnrollments')
        .select('personID')
        .eq('moduleID', module.prerequisiteModuleID)
        .in('status', ['COMPLETED', 'GRADUATED'])
        .in('personID', personIDs);

    const completedSet = new Set(completedEnrollments?.map(e => e.personID) || []);
    return {
        eligible: personIDs.filter(id => completedSet.has(id)),
        ineligible: personIDs.filter(id => !completedSet.has(id)),
    };
};

const enrollPeople = async ({ moduleID, personIDs, enrolledByID, skipPrerequisiteCheck = false }) => {
    const orgID = await getCurrentOrgID();

    let idsToEnroll = personIDs;

    if (!skipPrerequisiteCheck) {
        const { eligible, ineligible } = await checkPrerequisite(moduleID, personIDs);
        if (ineligible.length > 0 && eligible.length === 0) {
            Toast.show({ type: 'error', text2: `All selected people haven't completed the prerequisite module` });
            throw Error('prerequisite_not_met');
        }
        if (ineligible.length > 0) {
            Toast.show({ type: 'info', text2: `${ineligible.length} ${ineligible.length === 1 ? 'person' : 'people'} skipped (prerequisite not met)` });
        }
        idsToEnroll = eligible;
    }

    if (idsToEnroll.length === 0) return [];

    const enrollments = idsToEnroll.map(personID => ({
        organizationID: orgID,
        moduleID,
        personID,
        enrolledByID,
        status: 'ENROLLED',
    }));

    const { error, data } = await supabase.from('TrainingEnrollments').upsert(enrollments, {
        onConflict: 'moduleID,personID,status',
        ignoreDuplicates: true,
    }).select('*, Person:People!TrainingEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)');

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "enrollPeople", error, moduleID, personIDs: idsToEnroll }, `Something went wrong enrolling people`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: `${idsToEnroll.length} ${idsToEnroll.length === 1 ? 'person' : 'people'} enrolled!` });
    return data;
};

const getRoleMembersForEnrollment = async (roleID) => {
    const { error, data } = await supabase.from('PeopleRoles')
        .select('Person:People!inner(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
        .eq('roleID', roleID);

    if (error) {
        ErrorHandler(Error(error.message), { query: "getRoleMembersForEnrollment", error, roleID }, `Something went wrong fetching role members`);
        return [];
    }

    return data?.map(d => d.Person).filter(Boolean) || [];
};

const unenrollPerson = async ({ enrollmentID }) => {
    const { error, data } = await supabase.from('TrainingEnrollments')
        .update({ status: 'UNENROLLED', unenrolledAt: new Date() })
        .eq('id', enrollmentID)
        .select()
        .maybeSingle();

    // 23505: duplicate key — a row with status='UNENROLLED' already exists for
    // this person+module (happens when re-enrolling after unenrolling). Just
    // delete the current enrollment instead since the unenrolled state is
    // already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "unenrollPerson", error: deleteError, enrollmentID }, `Something went wrong unenrolling this person`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person unenrolled' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "unenrollPerson", error, enrollmentID }, `Something went wrong unenrolling this person`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Person unenrolled' });
    return data;
};

const dropEnrollment = async ({ enrollmentID }) => {
    const { error, data } = await supabase.from('TrainingEnrollments')
        .update({ status: 'DROPPED', unenrolledAt: new Date() })
        .eq('id', enrollmentID)
        .select()
        .maybeSingle();

    // 23505: duplicate key — a row with status='DROPPED' already exists for
    // this person+module (happens when re-enrolling after dropping). Just
    // delete the current enrollment instead since the dropped state is
    // already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "dropEnrollment", error: deleteError, enrollmentID }, `Something went wrong dropping this person`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person marked as dropped' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "dropEnrollment", error, enrollmentID }, `Something went wrong dropping this person`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Person marked as dropped' });
    return data;
};

const completeEnrollment = async ({ enrollmentID }) => {
    const { error, data } = await supabase.from('TrainingEnrollments')
        .update({ status: 'COMPLETED', completedAt: new Date() })
        .eq('id', enrollmentID)
        .select('*, Module:TrainingModules!TrainingEnrollments_moduleID_fkey(id, name)')
        .maybeSingle();

    // 23505: duplicate key — a row with status='COMPLETED' already exists for
    // this person+module (happens when re-enrolling after completing). Just
    // delete the current enrollment instead since the completed state is
    // already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "completeEnrollment", error: deleteError, enrollmentID }, `Something went wrong completing this enrollment`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person already completed this module' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "completeEnrollment", error, enrollmentID }, `Something went wrong completing this enrollment`);
        throw Error();
    }

    // Check if any modules use this module as a prerequisite
    let dependentModules = [];
    if (data?.moduleID) {
        const { data: deps } = await supabase.from('TrainingModules')
            .select('id, name')
            .eq('prerequisiteModuleID', data.moduleID)
            .eq('status', 'ACTIVE');
        dependentModules = deps || [];
    }

    Toast.delayShow({ type: 'success', text2: 'Module completed!' });
    return { ...data, dependentModules };
};

const graduateEnrollment = async ({ enrollmentID }) => {
    const { error, data } = await supabase.from('TrainingEnrollments')
        .update({ status: 'GRADUATED' })
        .eq('id', enrollmentID)
        .select()
        .maybeSingle();

    // 23505: duplicate key — a row with status='GRADUATED' already exists for
    // this person+module (happens when re-enrolling after graduating). Just
    // delete the current enrollment instead since the graduated state is
    // already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "graduateEnrollment", error: deleteError, enrollmentID }, `Something went wrong graduating this enrollment`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person already graduated this module' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "graduateEnrollment", error, enrollmentID }, `Something went wrong graduating this enrollment`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Student graduated!' });
    return data;
};

const deleteTrainingEnrollment = async ({ enrollmentID }) => {
    const { error } = await supabase.from('TrainingEnrollments').delete().eq('id', enrollmentID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteTrainingEnrollment", error, enrollmentID }, `Something went wrong deleting this enrollment`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Enrollment deleted' });
};


// ============================================================
// CLASS COMPLETIONS
// ============================================================

const markClassCompleted = async ({ enrollmentID, classID, markedByID, classInstanceID }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('TrainingClassCompletions').upsert({
        organizationID: orgID,
        enrollmentID,
        classID,
        markedByID,
        classInstanceID: classInstanceID || null,
        completedAt: new Date(),
    }, { onConflict: 'enrollmentID,classID' }).select().maybeSingle();

    // 23505: duplicate key — the auto-completion trigger tried to set this
    // enrollment to COMPLETED but a row with status='COMPLETED' already exists
    // for this person+module. Just delete the current enrollment instead since
    // the completed state is already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "markClassCompleted", error: deleteError, enrollmentID, classID }, `Something went wrong marking the class as completed`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person already completed this module' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "markClassCompleted", error, enrollmentID, classID }, `Something went wrong marking the class as completed`);
        throw Error();
    }

    return data;
};

const unmarkClassCompleted = async ({ enrollmentID, classID }) => {
    const { error } = await supabase.from('TrainingClassCompletions')
        .delete()
        .eq('enrollmentID', enrollmentID)
        .eq('classID', classID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "unmarkClassCompleted", error, enrollmentID, classID }, `Something went wrong unmarking the class completion`);
        throw Error();
    }
};


// ============================================================
// REQUIREMENT COMPLETIONS
// ============================================================

const markRequirementCompleted = async ({ enrollmentID, requirementID, markedByID, notes }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('TrainingRequirementCompletions').upsert({
        organizationID: orgID,
        enrollmentID,
        requirementID,
        markedByID,
        notes: notes || null,
        completedAt: new Date(),
    }, { onConflict: 'enrollmentID,requirementID' }).select().maybeSingle();

    // 23505: duplicate key — the auto-completion trigger tried to set this
    // enrollment to COMPLETED but a row with status='COMPLETED' already exists
    // for this person+module. Just delete the current enrollment instead since
    // the completed state is already tracked by the other row.
    if (error) {
        if (error.code === '23505') {
            const { error: deleteError } = await supabase.from('TrainingEnrollments')
                .delete()
                .eq('id', enrollmentID);

            if (deleteError) {
                ErrorHandler(Error(deleteError.message), { mutation: "markRequirementCompleted", error: deleteError, enrollmentID, requirementID }, `Something went wrong marking the requirement as completed`);
                throw Error();
            }
            Toast.delayShow({ type: 'success', text2: 'Person already completed this module' });
            return null;
        }
        ErrorHandler(Error(error.message), { mutation: "markRequirementCompleted", error, enrollmentID, requirementID }, `Something went wrong marking the requirement as completed`);
        throw Error();
    }

    return data;
};

const unmarkRequirementCompleted = async ({ enrollmentID, requirementID }) => {
    const { error } = await supabase.from('TrainingRequirementCompletions')
        .delete()
        .eq('enrollmentID', enrollmentID)
        .eq('requirementID', requirementID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "unmarkRequirementCompleted", error, enrollmentID, requirementID }, `Something went wrong unmarking the requirement completion`);
        throw Error();
    }
};


// ============================================================
// CLASS INSTANCES (Scheduling)
// ============================================================

const createClassInstance = async ({ classID, moduleID, scheduledAt, notes, teacherID, termID }) => {
    const { data: response, error: invokeError } = await supabase.functions.invoke('training-schedule', {
        body: { action: 'upsertInstance', moduleID, classID, scheduledAt, notes, teacherID, termID },
    });

    if (invokeError) {
        ErrorHandler(Error(invokeError.message), { mutation: "createClassInstance", invokeError, classID }, `Something went wrong creating the class instance`);
        throw Error();
    }
    if (response?.success === false) {
        ErrorHandler(Error(response?.message), { mutation: "createClassInstance", response, classID }, response?.message || `Something went wrong creating the class instance`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Class instance scheduled!' });
    return response?.instance;
};

const updateClassInstance = async ({ instanceID, classID, moduleID, scheduledAt, notes, teacherID, termID }) => {
    const { data: response, error: invokeError } = await supabase.functions.invoke('training-schedule', {
        body: { action: 'upsertInstance', instanceID, moduleID, classID, scheduledAt, notes, teacherID, termID },
    });

    if (invokeError) {
        ErrorHandler(Error(invokeError.message), { mutation: "updateClassInstance", invokeError, instanceID }, `Something went wrong updating the class instance`);
        throw Error();
    }
    if (response?.success === false) {
        ErrorHandler(Error(response?.message), { mutation: "updateClassInstance", response, instanceID }, response?.message || `Something went wrong updating the class instance`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Class instance updated!' });
    return response?.instance;
};

const getClassInstance = async (classInstanceID) => {
    const { error, data } = await supabase.from('TrainingClassInstances').select(`*,
        Class:TrainingClasses!inner(*,
            Module:TrainingModules(id, name, organizationID,
                Enrollments:TrainingEnrollments(id, personID, status,
                    Person:People!TrainingEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
                    Completions:TrainingClassCompletions(id, classID, classInstanceID, completedAt, markedByID)
                )
            )
        ),
        Teacher:People!TrainingClassInstances_teacherID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Term:TrainingClassTerms!TrainingClassInstances_termID_fkey(id, name),
        Completions:TrainingClassCompletions(id, enrollmentID, classID, completedAt, markedByID, classInstanceID,
            Enrollment:TrainingEnrollments(id, personID,
                Person:People!TrainingEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)
            )
        )
    `)
        .eq('id', classInstanceID)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getClassInstance", classInstanceID }, `Something went wrong fetching the class instance`);
        return null;
    }

    return data;
};

const getTrainingClassInstanceByTaskInstanceID = async (taskInstanceID) => {
    const { error, data } = await supabase.from('TrainingClassInstances').select(`id, scheduledAt, classID,
        Class:TrainingClasses!inner(id, name,
            Module:TrainingModules(id, name)
        )
    `)
        .eq('taskInstanceID', taskInstanceID)
        .limit(1);

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingClassInstanceByTaskInstanceID", taskInstanceID }, `Something went wrong fetching the training class instance`);
        return null;
    }

    return data?.[0] || null;
};

const deleteClassInstance = async (classInstanceID) => {
    const { data: response, error: invokeError } = await supabase.functions.invoke('training-schedule', {
        body: { action: 'deleteInstance', instanceID: classInstanceID },
    });

    if (invokeError) {
        ErrorHandler(Error(invokeError.message), { mutation: "deleteClassInstance", invokeError, classInstanceID }, `Something went wrong deleting the class instance`);
        throw Error();
    }
    if (response?.success === false) {
        ErrorHandler(Error(response?.message), { mutation: "deleteClassInstance", response, classInstanceID }, response?.message || `Something went wrong deleting the class instance`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Class instance deleted' });
};

// ============================================================
// TRAINING CLASS TERMS
// ============================================================

const getTrainingTerms = async (moduleID) => {
    const orgID = await getCurrentOrgID();
    const { error, data } = await supabase.from('TrainingClassTerms')
        .select('id, name, description, createdAt')
        .eq('organizationID', orgID)
        .eq('moduleID', moduleID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingTerms", moduleID }, `Something went wrong fetching the training terms`);
        return [];
    }

    return data;
};

const saveTrainingTerm = async ({ termID, moduleID, name, description, rows }) => {
    const { data: response, error: invokeError } = await supabase.functions.invoke('training-schedule', {
        body: { action: 'saveTerm', termID, moduleID, name, description, rows },
    });

    if (invokeError) {
        ErrorHandler(Error(invokeError.message), { mutation: "saveTrainingTerm", invokeError, termID, moduleID }, `Something went wrong saving the training term`);
        throw Error();
    }
    if (response?.success === false) {
        ErrorHandler(Error(response?.message), { mutation: "saveTrainingTerm", response, termID, moduleID }, response?.message || `Something went wrong saving the training term`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: termID ? 'Term updated!' : 'Term created!' });
    return response;
};

const deleteTrainingTerm = async (termID) => {
    const { data: response, error: invokeError } = await supabase.functions.invoke('training-schedule', {
        body: { action: 'deleteTerm', termID },
    });

    if (invokeError) {
        ErrorHandler(Error(invokeError.message), { mutation: "deleteTrainingTerm", invokeError, termID }, `Something went wrong deleting the training term`);
        throw Error();
    }
    if (response?.success === false) {
        ErrorHandler(Error(response?.message), { mutation: "deleteTrainingTerm", response, termID }, response?.message || `Something went wrong deleting the training term`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Term deleted' });
};


// ============================================================
// FILE ATTACHMENTS
// ============================================================

const uploadTrainingFile = async ({ moduleID, classID, file, uploadedByID }) => {
    const orgID = await getCurrentOrgID();
    const storageKey = `training/${moduleID}/${classID}/${file.name}`;

    const result = await saveFile(file.uri, storageKey);

    if (!result) {
        ErrorHandler(Error("File upload failed"), { mutation: "uploadTrainingFile", moduleID }, `Something went wrong uploading the file`);
        throw Error();
    }

    const { error, data } = await supabase.from('TrainingFileAttachments').insert({
        organizationID: orgID,
        moduleID: moduleID || null,
        classID,
        storageKey,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.mimeType,
        uploadedByID,
    }).select().maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "uploadTrainingFile-record", error }, `File uploaded but something went wrong saving the record`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: `File "${file.name}" uploaded!` });
    return data;
};

const downloadTrainingFile = async (storageKey) => {
    const orgID = await getCurrentOrgID();
    const fullKey = storageKey.startsWith(orgID + '/') ? storageKey : `${orgID}/${storageKey}`;

    const { data, error } = await supabase.storage
        .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
        .createSignedUrl(fullKey, 3600);

    if (error) {
        ErrorHandler(Error(error.message), { query: 'downloadTrainingFile', storageKey }, 'Something went wrong downloading the file');
        Toast.show({ type: 'error', text2: 'Could not download file' });
        return null;
    }

    return data.signedUrl;
};

const deleteTrainingFile = async ({ fileID, storageKey }) => {
    await removeStorageFile(storageKey);

    const { error } = await supabase.from('TrainingFileAttachments').delete().eq('id', fileID);
    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteTrainingFile", error, fileID }, `Something went wrong deleting the file record`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'File deleted' });
};


// ============================================================
// STATISTICS
// ============================================================

const getTrainingOverviewStats = async () => {
    const orgID = await getCurrentOrgID();
    const { error, data } = await supabase.rpc('get_training_overview_stats', { p_org_id: orgID });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingOverviewStats", error }, `Something went wrong fetching training statistics`);
        return null;
    }

    return data?.[0] || data;
};

const getTrainingModuleStats = async (moduleID) => {
    const { error, data } = await supabase.rpc('get_training_module_stats', { p_module_id: moduleID });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getTrainingModuleStats", error, moduleID }, `Something went wrong fetching module statistics`);
        return [];
    }

    return data;
};

const getUpcomingTrainingClasses = async (personID) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('TrainingEnrollments')
        .select(`
            id, moduleID,
            Module:TrainingModules!TrainingEnrollments_moduleID_fkey(
                id, name,
                Classes:TrainingClasses(
                    id, name,
                    Instances:TrainingClassInstances(id, scheduledAt, notes)
                )
            )
        `)
        .eq('personID', personID)
        .eq('organizationID', orgID)
        .eq('status', 'ENROLLED');

    if (error) {
        ErrorHandler(Error(error.message), { query: "getUpcomingTrainingClasses", error, personID }, `Something went wrong fetching upcoming training classes`);
        return [];
    }

    const now = new Date();
    const instances = [];
    for (const enrollment of (data || [])) {
        for (const cls of (enrollment.Module?.Classes || [])) {
            for (const inst of (cls.Instances || [])) {
                if (new Date(inst.scheduledAt) >= now) {
                    instances.push({
                        ...inst,
                        className: cls.name,
                        classID: cls.id,
                        moduleName: enrollment.Module?.name,
                        moduleID: enrollment.moduleID,
                    });
                }
            }
        }
    }

    return instances.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)).slice(0, 10);
};

const getPersonTrainingProgress = async (personID) => {
    if (!personID || personID === 'undefined') return [];
    const { error, data } = await supabase.rpc('get_person_training_progress', { p_person_id: personID });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getPersonTrainingProgress", error, personID }, `Something went wrong fetching training progress`);
        return [];
    }

    return data;
};


export {
    getTrainingModules,
    getTrainingModule,
    saveTrainingModule,
    deleteTrainingModule,
    saveTrainingClass,
    deleteTrainingClass,
    reorderTrainingClasses,
    saveTrainingRequirement,
    deleteTrainingRequirement,
    enrollPeople,
    getRoleMembersForEnrollment,
    checkPrerequisite,
    unenrollPerson,
    dropEnrollment,
    completeEnrollment,
    graduateEnrollment,
    deleteTrainingEnrollment,
    markClassCompleted,
    unmarkClassCompleted,
    markRequirementCompleted,
    unmarkRequirementCompleted,
    createClassInstance,
    getClassInstance,
    getTrainingClassInstanceByTaskInstanceID,
    updateClassInstance,
    deleteClassInstance,
    getTrainingTerms,
    saveTrainingTerm,
    deleteTrainingTerm,
    uploadTrainingFile,
    downloadTrainingFile,
    deleteTrainingFile,
    getTrainingOverviewStats,
    getTrainingModuleStats,
    getUpcomingTrainingClasses,
    getPersonTrainingProgress,
};
